import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useEffect, useState, type FormEvent } from 'react';
import { partyDirectoryApi, type ContactView } from './party-directory-api';
import { PrototypeIcon } from './PrototypeIcon';

export function ClientContacts({
  clientId,
  clientName,
  clientLogo,
  clientLogoVersion,
  csrfToken,
  onFeedback,
}: {
  readonly clientId: string;
  readonly clientName: string;
  readonly clientLogo: boolean;
  readonly clientLogoVersion: number;
  readonly csrfToken: string;
  readonly onFeedback?: (message: string | null) => void;
}): React.JSX.Element {
  const [contacts, setContacts] = useState<readonly ContactView[]>([]);
  const [suggestions, setSuggestions] = useState<readonly ContactView[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<ContactView | null>(null);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ContactView | null>(null);
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [photoRevision, setPhotoRevision] = useState<Record<string, number>>({});

  const refresh = async (): Promise<void> => {
    const view = await partyDirectoryApi.contacts(clientId);
    setContacts(view.contacts);
    setSuggestions(view.suggestions);
  };

  useEffect(() => {
    let active = true;
    void partyDirectoryApi.contacts(clientId).then(
      (view) => {
        if (active) {
          setContacts(view.contacts);
          setSuggestions(view.suggestions);
        }
      },
      () => {
        if (active) setError('Contacts indisponibles.');
      },
    );
    return () => {
      active = false;
    };
  }, [clientId]);

  const add = async (fields: {
    sourceContactId?: string;
    displayName?: string;
    email?: string | null;
  }): Promise<boolean> => {
    setBusy(true);
    try {
      await partyDirectoryApi.addContact(clientId, fields, csrfToken);
      await refresh();
      setName('');
      setEmail('');
      setError(null);
      return true;
    } catch {
      setError('Contact non ajouté. Vérifiez sa relation avec le Client et actualisez la page.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!name.trim()) return;
    if (editing) {
      setBusy(true);
      void partyDirectoryApi
        .updateContact(
          clientId,
          editing.id,
          {
            displayName: name.trim(),
            email: email || null,
            phone: phone || null,
            position: position || null,
          },
          csrfToken,
        )
        .then(
          async () => {
            await refresh();
            setEditing(null);
            setError(null);
            if (onFeedback) onFeedback('Contact modifié.');
            else setNotice('Contact modifié.');
          },
          () => setError('Contact non modifié. Vérifiez les informations saisies.'),
        )
        .finally(() => setBusy(false));
    } else {
      void add({ displayName: name, email: email || null }).then((saved) => {
        if (saved) setAdding(false);
      });
    }
  };
  const dismiss = async (sourceContactId: string): Promise<void> => {
    setBusy(true);
    try {
      await partyDirectoryApi.dismissContactSuggestion(clientId, sourceContactId, csrfToken);
      await refresh();
      setError(null);
    } catch {
      setError('Impossible de masquer cette proposition pour le moment.');
    } finally {
      setBusy(false);
    }
  };
  const uploadPhoto = async (contactId: string, file: File): Promise<void> => {
    setBusy(true);
    try {
      await partyDirectoryApi.saveContactPhoto(clientId, contactId, file, csrfToken);
      await refresh();
      setPhotoRevision((current) => ({ ...current, [contactId]: (current[contactId] ?? 0) + 1 }));
      setError(null);
      if (onFeedback) onFeedback('Photo du contact enregistrée.');
      else setNotice('Photo du contact enregistrée.');
    } catch {
      setError('Photo non enregistrée. Choisissez un PNG, JPEG ou WebP de 512 Ko maximum.');
    } finally {
      setBusy(false);
    }
  };

  const identity = (contact: ContactView, editable: boolean): React.JSX.Element => {
    const portrait = (
      <>
        <span className="client-directory__contact-avatar" aria-hidden="true">
          {contact.hasPhoto ? (
            <img
              src={`/api/directory/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contact.id)}/photo?v=${photoRevision[contact.id] ?? 0}`}
              alt=""
            />
          ) : (
            contact.displayName
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part[0])
              .join('')
              .toLocaleUpperCase('fr-FR')
          )}
        </span>
        <span className="client-directory__contact-company" aria-label={`Logo de ${clientName}`}>
          {clientLogo ? (
            <img
              src={`/api/clients/${encodeURIComponent(clientId)}/logo?v=${clientLogoVersion}`}
              alt=""
            />
          ) : (
            clientName.slice(0, 2).toLocaleUpperCase('fr-FR')
          )}
        </span>
      </>
    );
    return (
      <div className="client-directory__contact-identity">
        {editable ? (
          <label className="client-directory__contact-portrait client-directory__contact-portrait--editable">
            {portrait}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-label={`Ajouter ou changer la photo de ${contact.displayName}`}
              disabled={busy}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) void uploadPhoto(contact.id, file);
                event.currentTarget.value = '';
              }}
            />
          </label>
        ) : (
          <div className="client-directory__contact-portrait">{portrait}</div>
        )}
        <strong>{contact.displayName}</strong>
        <span className="client-directory__contact-position">
          {contact.position || 'Rôle à préciser'}
        </span>
        <span className="client-directory__contact-email">
          {contact.email || 'E-mail à confirmer'}
        </span>
        <span className="client-directory__contact-phone">
          {contact.phone || 'Téléphone à préciser'}
        </span>
      </div>
    );
  };

  return (
    <section className="client-directory__contacts" aria-label="Contacts du Client">
      {notice && !onFeedback && (
        <p role="status" className="client-directory__success tosho-feedback is-success">
          {notice}
        </p>
      )}
      {error && !adding && !editing && !inviteTarget && (
        <p role="alert" className="client-directory__error tosho-feedback is-error">
          {error}
        </p>
      )}
      <h3>Contacts rattachés</h3>
      <button
        className="client-directory__contact-add"
        type="button"
        onClick={() => setAdding(true)}
      >
        <PrototypeIcon name="plus" /> Ajouter un contact
      </button>
      {contacts.length === 0 && <p>Aucun contact confirmé.</p>}
      <ul className="client-directory__contact-grid">
        {contacts.map((contact) => (
          <li key={contact.id} className="client-directory__contact-card">
            <button
              className="client-directory__contact-edit"
              type="button"
              aria-label={`Modifier ${contact.displayName}`}
              title={`Modifier ${contact.displayName}`}
              onClick={() => {
                setEditing(contact);
                setName(contact.displayName);
                setEmail(contact.email ?? '');
                setPhone(contact.phone ?? '');
                setPosition(contact.position ?? '');
                setError(null);
              }}
            >
              <PrototypeIcon name="pencil" />
            </button>
            {identity(contact, true)}
            {contact.email && (
              <button
                type="button"
                className="client-directory__contact-invite"
                onClick={() => {
                  setInviteTarget(contact);
                  setNotice(null);
                  onFeedback?.(null);
                  setError(null);
                }}
              >
                <PrototypeIcon name="send" /> Inviter
              </button>
            )}
          </li>
        ))}
      </ul>
      {suggestions.length > 0 && (
        <>
          <h4>Contacts proposés par Freebe</h4>
          <ul className="client-directory__contact-grid">
            {suggestions.map((contact) => (
              <li key={contact.id} className="client-directory__contact-card">
                {identity(contact, false)}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Confirmer le rattachement de cette personne au Client ?'))
                      void add({ sourceContactId: contact.id });
                  }}
                >
                  <PrototypeIcon name="plus" /> Confirmer le contact
                </button>
                <button type="button" disabled={busy} onClick={() => void dismiss(contact.id)}>
                  Ne plus proposer ce contact
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {(adding || editing) && (
        <ModalBackdrop
          className="client-directory__overlay client-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setAdding(false);
              setEditing(null);
            }
          }}
          aria-label={editing ? 'Modifier le contact' : 'Ajouter un contact'}
          close={
            <ModalCloseButton
              onClick={() => {
                setAdding(false);
                setEditing(null);
              }}
            />
          }
        >
          <form
            className="tosho-modal-panel client-directory__modal client-directory__contact-dialog"
            onSubmit={submit}
          >
            <header className="tosho-modal-heading tosho-modal-header">
              <h3>
                <PrototypeIcon name={editing ? 'settings' : 'plus'} />{' '}
                {editing ? 'Modifier le contact' : 'Ajouter un contact'}
              </h3>
            </header>
            {error && (
              <p role="alert" className="client-directory__error tosho-feedback is-error">
                {error}
              </p>
            )}
            <label>
              Nom du Contact{' '}
              <input
                value={name}
                maxLength={255}
                onChange={(event) => setName(event.currentTarget.value)}
                required
              />
            </label>
            <label>
              E-mail (facultatif){' '}
              <input
                type="email"
                value={email}
                maxLength={320}
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            </label>
            {editing && (
              <>
                <label>
                  Téléphone
                  <input
                    value={phone}
                    maxLength={32}
                    onChange={(event) => setPhone(event.currentTarget.value)}
                  />
                </label>
                <label>
                  Fonction
                  <input
                    value={position}
                    maxLength={255}
                    onChange={(event) => setPosition(event.currentTarget.value)}
                  />
                </label>
              </>
            )}
            <button type="submit" className="client-directory__primary" disabled={busy}>
              <PrototypeIcon name={editing ? 'settings' : 'plus'} />{' '}
              {editing ? 'Enregistrer' : 'Ajouter le contact'}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setEditing(null);
              }}
            >
              Annuler
            </button>
          </form>
        </ModalBackdrop>
      )}
      {inviteTarget?.email && (
        <ModalBackdrop
          className="client-directory__overlay client-directory"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setInviteTarget(null);
          }}
          close={<ModalCloseButton onClick={() => setInviteTarget(null)} />}
        >
          <form
            className="tosho-modal-panel client-directory__modal client-directory__contact-dialog"
            onSubmit={(event) => {
              event.preventDefault();
              if (!inviteTarget.email) return;
              setBusy(true);
              void partyDirectoryApi
                .inviteClientContact(
                  clientId,
                  inviteTarget.id,
                  inviteTarget.email,
                  givenName,
                  familyName,
                  csrfToken,
                )
                .then(
                  (result) => {
                    const message =
                      result.status === 'ACTIVE'
                        ? 'Compte déjà actif et rattaché.'
                        : 'Invitation envoyée.';
                    if (onFeedback) onFeedback(message);
                    else setNotice(message);
                    setInviteTarget(null);
                    setError(null);
                  },
                  () =>
                    setError(
                      'Invitation impossible. Vérifiez l’identité et l’adresse, puis réessayez.',
                    ),
                )
                .finally(() => setBusy(false));
            }}
          >
            <header className="tosho-modal-heading tosho-modal-header">
              <h4>
                <PrototypeIcon name="plus" /> Inviter {inviteTarget.displayName}
              </h4>
            </header>
            {error && (
              <p role="alert" className="client-directory__error tosho-feedback is-error">
                {error}
              </p>
            )}
            <p>
              E-mail confirmé : {inviteTarget.email}. Le compte n'obtient aucun tableau
              automatiquement.
            </p>
            <label>
              Prénom{' '}
              <input
                value={givenName}
                maxLength={100}
                required
                onChange={(event) => setGivenName(event.currentTarget.value)}
              />
            </label>
            <label>
              Nom{' '}
              <input
                value={familyName}
                maxLength={100}
                required
                onChange={(event) => setFamilyName(event.currentTarget.value)}
              />
            </label>
            <button type="submit" className="client-directory__primary" disabled={busy}>
              Envoyer l’invitation
            </button>
            <button type="button" onClick={() => setInviteTarget(null)}>
              Annuler
            </button>
          </form>
        </ModalBackdrop>
      )}
    </section>
  );
}
